import React, { useState, useEffect } from 'react';
import { X, FileText, Settings, Ruler, Layout, Type, Star } from 'lucide-react';
import { BackendFont, BackendFontService } from '../services/BackendFontService';
import { UserPageSetupDefaultsService } from '../services/UserPageSetupDefaultsService';

// Backend-compatible page setup types (from validators.ts)
export interface PageSetupState {
  pageSize: 'Letter' | 'A4' | 'Legal' | 'Tabloid' | { widthPx: number; heightPx: number };
  dpi: number; // 72-600, common: 96, 150, 192, 300, 600
  margins: {
    top: number;    // pixels, max 2000
    right: number;  // pixels, max 2000  
    bottom: number; // pixels, max 2000
    left: number;   // pixels, max 2000
  };
  columns: number; // 1-1 (backend currently only supports 1)
  hyphenation: boolean;
  bidi: 'auto' | 'ltr' | 'rtl';
  orientation: 'portrait' | 'landscape';
  // Font settings (sent via styles, not pageSetup)
  fontFamily?: string;
  fontSize?: number; // points
  fontColor?: string;
}

interface PageSetupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentSetup: PageSetupState;
  onApply: (setup: PageSetupState) => void;
  onPreview?: (setup: PageSetupState) => void;
}

// Standard page sizes in inches (converted to pixels at runtime)
const PAGE_SIZES = {
  Letter: { width: 8.5, height: 11, label: 'Letter (8.5" × 11")' },
  A4: { width: 8.27, height: 11.69, label: 'A4 (8.27" × 11.69")' },
  Legal: { width: 8.5, height: 14, label: 'Legal (8.5" × 14")' },
  Tabloid: { width: 11, height: 17, label: 'Tabloid (11" × 17")' }
};

// Common DPI presets
const DPI_PRESETS = [
  { value: 96, label: '96 DPI (Screen)', description: 'Standard screen resolution' },
  { value: 150, label: '150 DPI (Draft)', description: 'Draft print quality' },
  { value: 192, label: '192 DPI (Good)', description: 'Good print quality' },
  { value: 300, label: '300 DPI (High)', description: 'High print quality' },
  { value: 600, label: '600 DPI (Best)', description: 'Best print quality' }
];

export const PageSetupDialog: React.FC<PageSetupDialogProps> = ({
  isOpen,
  onClose,
  currentSetup,
  onApply,
  onPreview
}) => {
  const [setup, setSetup] = useState<PageSetupState>(currentSetup);
  const [activeTab, setActiveTab] = useState<'page' | 'margins' | 'layout' | 'typography'>('page');
  const [customSize, setCustomSize] = useState({ width: 8.5, height: 11 });
  const [availableFonts, setAvailableFonts] = useState<BackendFont[]>([]);
  const [fontsLoading, setFontsLoading] = useState(true);
  
  // User defaults state (global scope only)
  const [userDefaultsService] = useState(() => new UserPageSetupDefaultsService());
  const [showDefaultsSection, setShowDefaultsSection] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSetup(currentSetup);
      // Extract custom size if needed
      if (typeof currentSetup.pageSize === 'object') {
        const dpi = currentSetup.dpi;
        setCustomSize({
          width: currentSetup.pageSize.widthPx / dpi,
          height: currentSetup.pageSize.heightPx / dpi
        });
      }
    }
  }, [isOpen, currentSetup]);

  // Load available fonts when dialog opens
  useEffect(() => {
    if (isOpen && fontsLoading) {
      loadAvailableFonts();
    }
  }, [isOpen, fontsLoading]);

  // Initialize user defaults when dialog opens
  useEffect(() => {
    if (isOpen) {
      initializeUserDefaults();
    }
  }, [isOpen]);

  const loadAvailableFonts = async () => {
    try {
      setFontsLoading(true);
      console.log('[PageSetupDialog] Loading available fonts...');
      
      // Get authentication token (no fallback - must be valid)
      const authToken = localStorage.getItem('webelieve_access_token');
      if (!authToken) {
        throw new Error('No authentication token available');
      }
      
      // Create backend font service directly with current origin as DMS base URL
      const baseUrl = window.location.origin;
      const backendFontService = new BackendFontService(baseUrl, authToken);
      
      // Initialize the backend font service
      await backendFontService.initialize();
      
      // Get available fonts from the backend service
      const fonts = backendFontService.getAvailableFonts();
      console.log('[PageSetupDialog] Loaded fonts:', fonts);
      setAvailableFonts(fonts);
    } catch (error) {
      console.error('[PageSetupDialog] Failed to load fonts:', error);
      // No fallback fonts - let the error propagate
      setAvailableFonts([]);
    } finally {
      setFontsLoading(false);
    }
  };

  // Initialize user defaults (global scope only)
  const initializeUserDefaults = async () => {
    try {
      // Load user defaults to potentially pre-populate form
      const userDefaults = await userDefaultsService.getUserDefaults({});
      
      if (userDefaults) {
        console.log('[PageSetupDialog] Loaded user defaults:', userDefaults);
        // Could pre-populate form with user defaults here if desired
      }
    } catch (error) {
      console.warn('[PageSetupDialog] Failed to initialize user defaults:', error);
    }
  };

  // Set typography defaults (global scope only)
  const handleSetTypographyDefaults = async () => {
    await userDefaultsService.setTypographyDefaults({ type: 'global' }, {
      fontFamily: setup.fontFamily,
      fontSize: setup.fontSize,
      // Note: fontWeight, fontStyle, lineHeight would need to be added to PageSetupState
    });
  };

  // Set page defaults (global scope only)
  const handleSetPageDefaults = async () => {
    await userDefaultsService.setPageDefaults({ type: 'global' }, {
      pageSize: typeof setup.pageSize === 'string' ? setup.pageSize : 'Custom',
      dpi: setup.dpi,
      orientation: setup.orientation,
      columns: setup.columns,
      hyphenation: setup.hyphenation,
      bidi: setup.bidi
    });
  };

  // Set margin defaults (global scope only)
  const handleSetMarginDefaults = async () => {
    await userDefaultsService.setMarginDefaults({ type: 'global' }, setup.margins);
  };

  const handlePageSizeChange = (pageSize: string) => {
    if (pageSize === 'Custom') {
      const dpi = setup.dpi;
      setSetup(prev => ({
        ...prev,
        pageSize: {
          widthPx: Math.round(customSize.width * dpi),
          heightPx: Math.round(customSize.height * dpi)
        }
      }));
    } else {
      setSetup(prev => ({
        ...prev,
        pageSize: pageSize as 'Letter' | 'A4' | 'Legal' | 'Tabloid'
      }));
    }
  };

  const handleCustomSizeChange = (dimension: 'width' | 'height', value: number) => {
    const newCustomSize = { ...customSize, [dimension]: value };
    setCustomSize(newCustomSize);
    
    if (typeof setup.pageSize === 'object') {
      const dpi = setup.dpi;
      setSetup(prev => ({
        ...prev,
        pageSize: {
          widthPx: Math.round(newCustomSize.width * dpi),
          heightPx: Math.round(newCustomSize.height * dpi)
        }
      }));
    }
  };

  const handleDpiChange = (dpi: number) => {
    setSetup(prev => {
      const newSetup = { ...prev, dpi };
      
      // Update custom page size if using custom
      if (typeof prev.pageSize === 'object') {
        newSetup.pageSize = {
          widthPx: Math.round(customSize.width * dpi),
          heightPx: Math.round(customSize.height * dpi)
        };
      }
      
      return newSetup;
    });
  };

  const handleMarginChange = (side: keyof PageSetupState['margins'], value: number) => {
    setSetup(prev => ({
      ...prev,
      margins: {
        ...prev.margins,
        [side]: Math.max(0, Math.min(2000, value))
      }
    }));
  };

  const handlePreview = () => {
    onPreview?.(setup);
  };

  const handleApply = () => {
    onApply(setup);
    onClose();
  };

  const getCurrentPageSize = () => {
    if (typeof setup.pageSize === 'string') {
      return setup.pageSize;
    }
    return 'Custom';
  };

  const getEffectivePageSize = () => {
    const dpi = setup.dpi;
    let width: number, height: number;
    
    if (typeof setup.pageSize === 'string') {
      const size = PAGE_SIZES[setup.pageSize];
      width = size.width * dpi;
      height = size.height * dpi;
    } else {
      width = setup.pageSize.widthPx;
      height = setup.pageSize.heightPx;
    }
    
    if (setup.orientation === 'landscape') {
      [width, height] = [height, width];
    }
    
    return { width, height };
  };

  const getContentArea = () => {
    const { width, height } = getEffectivePageSize();
    const contentWidth = width - setup.margins.left - setup.margins.right;
    const contentHeight = height - setup.margins.top - setup.margins.bottom;
    return { contentWidth, contentHeight };
  };

  if (!isOpen) return null;

  const { width: pageWidth, height: pageHeight } = getEffectivePageSize();
  const { contentWidth, contentHeight } = getContentArea();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[1000px] max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-lex-accent" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Page Setup</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-140px)]">
          {/* Sidebar Tabs */}
          <div className="w-48 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 flex-shrink-0">
            <div className="space-y-2">
              <button
                onClick={() => setActiveTab('page')}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                  activeTab === 'page' 
                    ? 'bg-lex-accent-soft text-lex-accent' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                <FileText className="w-4 h-4" />
                Page Size
              </button>
              <button
                onClick={() => setActiveTab('margins')}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                  activeTab === 'margins' 
                    ? 'bg-lex-accent-soft text-lex-accent' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                <Ruler className="w-4 h-4" />
                Margins
              </button>
              <button
                onClick={() => setActiveTab('layout')}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                  activeTab === 'layout' 
                    ? 'bg-lex-accent-soft text-lex-accent' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                <Layout className="w-4 h-4" />
                Layout
              </button>
              <button
                onClick={() => setActiveTab('typography')}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                  activeTab === 'typography' 
                    ? 'bg-lex-accent-soft text-lex-accent' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                <Type className="w-4 h-4" />
                Typography
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 bg-white dark:bg-gray-800 overflow-y-auto">
            {activeTab === 'page' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Page Size & Orientation</h3>
                  
                  {/* Page Size */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Paper Size</label>
                    <select
                      value={getCurrentPageSize()}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {Object.entries(PAGE_SIZES).map(([key, size]) => (
                        <option key={key} value={key}>{size.label}</option>
                      ))}
                      <option value="Custom">Custom Size</option>
                    </select>
                  </div>

                  {/* Custom Size */}
                  {getCurrentPageSize() === 'Custom' && (
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <h4 className="font-medium mb-3 text-gray-900 dark:text-white">Custom Dimensions (inches)</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Width</label>
                          <input
                            type="number"
                            value={customSize.width}
                            onChange={(e) => handleCustomSizeChange('width', parseFloat(e.target.value) || 0)}
                            min="1"
                            max="50"
                            step="0.1"
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Height</label>
                          <input
                            type="number"
                            value={customSize.height}
                            onChange={(e) => handleCustomSizeChange('height', parseFloat(e.target.value) || 0)}
                            min="1"
                            max="50"
                            step="0.1"
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Orientation */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Orientation</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                        <input
                          type="radio"
                          name="orientation"
                          value="portrait"
                          checked={setup.orientation === 'portrait'}
                          onChange={(e) => setSetup(prev => ({ ...prev, orientation: e.target.value as 'portrait' | 'landscape' }))}
                          className="text-lex-accent"
                        />
                        Portrait
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                        <input
                          type="radio"
                          name="orientation"
                          value="landscape"
                          checked={setup.orientation === 'landscape'}
                          onChange={(e) => setSetup(prev => ({ ...prev, orientation: e.target.value as 'portrait' | 'landscape' }))}
                          className="text-lex-accent"
                        />
                        Landscape
                      </label>
                    </div>
                  </div>

                  {/* DPI/Resolution */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Resolution (DPI)</label>
                    <select
                      value={setup.dpi}
                      onChange={(e) => handleDpiChange(parseInt(e.target.value))}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {DPI_PRESETS.map(preset => (
                        <option key={preset.value} value={preset.value}>
                          {preset.label} - {preset.description}
                        </option>
                      ))}
                    </select>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Higher DPI = better quality, larger file sizes
                    </p>
                  </div>

                  {/* User Defaults Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900 dark:text-white">Save as Default</h4>
                      <button
                        onClick={() => setShowDefaultsSection(!showDefaultsSection)}
                        className="text-sm text-lex-accent hover:text-lex-accent-hover"
                      >
                        {showDefaultsSection ? 'Hide' : 'Show'} Options
                      </button>
                    </div>
                    
                    {showDefaultsSection && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Save current page settings as global default
                          </span>
                          <button
                            onClick={handleSetPageDefaults}
                            className="px-3 py-1 text-sm bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover transition-colors"
                          >
                            Set as Default
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'margins' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Page Margins</h3>
                  
                  {/* Margin Controls */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Top (px)</label>
                        <input
                          type="number"
                          value={setup.margins.top}
                          onChange={(e) => handleMarginChange('top', parseInt(e.target.value) || 0)}
                          min="0"
                          max="2000"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Bottom (px)</label>
                        <input
                          type="number"
                          value={setup.margins.bottom}
                          onChange={(e) => handleMarginChange('bottom', parseInt(e.target.value) || 0)}
                          min="0"
                          max="2000"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Left (px)</label>
                        <input
                          type="number"
                          value={setup.margins.left}
                          onChange={(e) => handleMarginChange('left', parseInt(e.target.value) || 0)}
                          min="0"
                          max="2000"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Right (px)</label>
                        <input
                          type="number"
                          value={setup.margins.right}
                          onChange={(e) => handleMarginChange('right', parseInt(e.target.value) || 0)}
                          min="0"
                          max="2000"
                          className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Margin Presets */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Quick Presets</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSetup(prev => ({ ...prev, margins: { top: 72, right: 72, bottom: 72, left: 72 } }))}
                        className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                      >
                        Normal (1")
                      </button>
                      <button
                        onClick={() => setSetup(prev => ({ ...prev, margins: { top: Math.round(prev.dpi * 0.25), right: Math.round(prev.dpi * 0.25), bottom: Math.round(prev.dpi * 0.25), left: Math.round(prev.dpi * 0.25) } }))}
                        className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                      >
                        Minimal (0.25")
                      </button>
                      <button
                        onClick={() => setSetup(prev => ({ ...prev, margins: { top: 36, right: 36, bottom: 36, left: 36 } }))}
                        className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                      >
                        Narrow (0.5")
                      </button>
                      <button
                        onClick={() => setSetup(prev => ({ ...prev, margins: { top: 108, right: 72, bottom: 108, left: 108 } }))}
                        className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                      >
                        Wide (1.5")
                      </button>
                    </div>
                  </div>

                  {/* User Defaults Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900 dark:text-white">Save as Default</h4>
                      <button
                        onClick={() => setShowDefaultsSection(!showDefaultsSection)}
                        className="text-sm text-lex-accent hover:text-lex-accent-hover"
                      >
                        {showDefaultsSection ? 'Hide' : 'Show'} Options
                      </button>
                    </div>
                    
                    {showDefaultsSection && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Save current margin settings as global default
                          </span>
                          <button
                            onClick={handleSetMarginDefaults}
                            className="px-3 py-1 text-sm bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover transition-colors"
                          >
                            Set as Default
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'layout' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Layout Options</h3>
                  
                  {/* Text Direction */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Text Direction</label>
                    <select
                      value={setup.bidi}
                      onChange={(e) => setSetup(prev => ({ ...prev, bidi: e.target.value as 'auto' | 'ltr' | 'rtl' }))}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="auto">Auto-detect</option>
                      <option value="ltr">Left-to-Right</option>
                      <option value="rtl">Right-to-Left</option>
                    </select>
                  </div>

                  {/* Hyphenation */}
                  <div className="mb-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={setup.hyphenation}
                        onChange={(e) => setSetup(prev => ({ ...prev, hyphenation: e.target.checked }))}
                        className="text-lex-accent"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Hyphenation</span>
                    </label>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Automatically hyphenate words at line breaks
                    </p>
                  </div>

                  {/* Columns (currently limited to 1) */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Columns</label>
                    <input
                      type="number"
                      value={setup.columns}
                      onChange={(e) => setSetup(prev => ({ ...prev, columns: Math.max(1, Math.min(1, parseInt(e.target.value) || 1)) }))}
                      min="1"
                      max="1"
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      disabled
                    />
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Multiple columns not yet supported
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'typography' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Typography Settings</h3>
                  
                  {/* Font Family */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Font Family</label>
                    <select
                      value={setup.fontFamily || (availableFonts[0]?.family || '')}
                      onChange={(e) => {
                        const fontFamily = e.target.value;
                        setSetup(prev => ({ ...prev, fontFamily }));
                      }}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      disabled={fontsLoading || availableFonts.length === 0}
                    >
                      {fontsLoading ? (
                        <option>Loading fonts...</option>
                      ) : (
                        <>
                          {/* Dynamic fonts from HarfBuzz manifest - grouped by family */}
                          {(() => {
                            // Group fonts by family name
                            const fontGroups = availableFonts.reduce((groups, font) => {
                              if (!groups[font.family]) {
                                groups[font.family] = [];
                              }
                              groups[font.family].push(font);
                              return groups;
                            }, {} as Record<string, typeof availableFonts>);

                            return Object.entries(fontGroups).map(([familyName, variants]) => {
                              if (variants.length === 1) {
                                // Single variant - show as simple option
                                const font = variants[0];
                                return (
                                  <option key={`${font.family}-${font.weight}-${font.style}`} value={font.family}>
                                    {font.family}
                                  </option>
                                );
                              } else {
                                // Multiple variants - use optgroup with family name as value for all variants
                                return (
                                  <optgroup key={familyName} label={familyName}>
                                    {variants.map((font) => {
                                      const variantLabel = font.weight === 700 ? 'Bold' : 
                                                         font.style === 'italic' ? 'Italic' : 'Regular';
                                      return (
                                        <option key={`${font.family}-${font.weight}-${font.style}`} value={font.family}>
                                          {variantLabel}
                                        </option>
                                      );
                                    })}
                                  </optgroup>
                                );
                              }
                            });
                          })()}
                        </>
                      )}
                    </select>
                    <p className="text-sm text-gray-600 mt-1">
                      {fontsLoading 
                        ? 'Loading fonts from backend...'
                        : availableFonts.length > 0
                          ? `${availableFonts.length} fonts available from backend`
                          : 'No fonts available - check backend connection'
                      }
                    </p>
                  </div>

                  {/* Font Size */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Font Size (points)</label>
                    <div className="flex items-center gap-4">
                      <input
                        type="number"
                        value={setup.fontSize || 12}
                        onChange={(e) => setSetup(prev => ({ ...prev, fontSize: Math.max(6, Math.min(72, parseInt(e.target.value) || 12)) }))}
                        min="6"
                        max="72"
                        className="w-24 p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <div className="flex gap-2">
                        {[8, 9, 10, 11, 12, 14, 16, 18, 20, 24].map(size => (
                          <button
                            key={size}
                            onClick={() => setSetup(prev => ({ ...prev, fontSize: size }))}
                            className={`px-2 py-1 text-sm rounded transition-colors ${
                              (setup.fontSize || 12) === size 
                                ? 'bg-lex-accent-soft text-lex-accent' 
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Standard legal document font sizes: 11pt (California), 12pt (Federal), 14pt (Large print)
                    </p>
                  </div>

                  {/* Font Color */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Text Color</label>
                    <div className="flex items-center gap-4">
                      <input
                        type="color"
                        value={setup.fontColor || '#000000'}
                        onChange={(e) => setSetup(prev => ({ ...prev, fontColor: e.target.value }))}
                        className="w-12 h-8 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={setup.fontColor || '#000000'}
                        onChange={(e) => setSetup(prev => ({ ...prev, fontColor: e.target.value }))}
                        placeholder="#000000"
                        className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <div className="flex gap-2">
                        {['#000000', '#333333', '#666666', '#0066cc', '#cc0000'].map(color => (
                          <button
                            key={color}
                            onClick={() => setSetup(prev => ({ ...prev, fontColor: color }))}
                            className="w-6 h-6 rounded border-2 border-gray-300"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Legal documents typically use black text
                    </p>
                  </div>

                  {/* Font Preview */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h4 className="font-medium mb-2 text-gray-900 dark:text-white">Preview</h4>
                    <div 
                      className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded"
                      style={{
                        fontFamily: setup.fontFamily || (availableFonts[0]?.family || 'serif'),
                        fontSize: `${(setup.fontSize || 12) * 1.2}px`, // Scale for preview
                        color: setup.fontColor || '#000000'
                      }}
                    >
                      The quick brown fox jumps over the lazy dog. This is a sample of your selected typography settings.
                    </div>
                  </div>

                  {/* User Defaults Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900 dark:text-white">Save as Default</h4>
                      <button
                        onClick={() => setShowDefaultsSection(!showDefaultsSection)}
                        className="text-sm text-lex-accent hover:text-lex-accent-hover"
                      >
                        {showDefaultsSection ? 'Hide' : 'Show'} Options
                      </button>
                    </div>
                    
                    {showDefaultsSection && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Save current typography settings as global default
                          </span>
                          <button
                            onClick={handleSetTypographyDefaults}
                            className="px-3 py-1 text-sm bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover transition-colors"
                          >
                            Set as Default
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Preview Panel */}
          <div className="w-64 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4 flex-shrink-0">
            <h4 className="font-medium mb-3 text-gray-900 dark:text-white">Page Preview</h4>
            
            {/* Page Preview */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4 mb-4">
              <div 
                className="bg-lex-accent-soft border border-lex-accent mx-auto"
                style={{
                  width: '120px',
                  height: `${120 * (pageHeight / pageWidth)}px`,
                  maxHeight: '160px'
                }}
              >
                <div 
                  className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600"
                  style={{
                    margin: `${(setup.margins.top / pageWidth) * 120}px ${(setup.margins.right / pageWidth) * 120}px ${(setup.margins.bottom / pageWidth) * 120}px ${(setup.margins.left / pageWidth) * 120}px`,
                    height: `calc(100% - ${((setup.margins.top + setup.margins.bottom) / pageWidth) * 120}px)`
                  }}
                />
              </div>
            </div>

            {/* Dimensions */}
            <div className="text-sm space-y-2 text-gray-700 dark:text-gray-300">
              <div>
                <span className="font-medium text-gray-900 dark:text-white">Page:</span><br />
                {Math.round(pageWidth)}×{Math.round(pageHeight)} px
              </div>
              <div>
                <span className="font-medium text-gray-900 dark:text-white">Content:</span><br />
                {Math.round(contentWidth)}×{Math.round(contentHeight)} px
              </div>
              <div>
                <span className="font-medium text-gray-900 dark:text-white">DPI:</span> {setup.dpi}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Changes will regenerate the document layout
          </div>
          <div className="flex gap-3">
            <button
              onClick={handlePreview}
              className="px-4 py-2 text-lex-accent hover:bg-lex-state-hover rounded-lg transition-colors"
            >
              Preview
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover rounded-lg transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageSetupDialog;